// seo(path: $path) {
// ... on SeoResults {
//     redirection {
//       code
//       uri
//     }
//     info {
//       canonicalUri
//       areaName
//       areaExpansions {
//         level
//         name
//         url
//       }
//     }
//     onPage {
//       description
//       title
//       robots
//       heading
//     }
//     breadcrumbs {
//       label
//       tracking
//       uri
//       i18nLabelKey
//       noAppend
//     }
//     internalLinks {
//       currentlyAvailable {
//         category
//         geo
//         propertyType
//         section
//         transactionType
//         rows {
//           links {
//             category
//             geo
//             propertyType
//             section
//             transactionType
//             uri
//           }
//         }
//         links {
//           category
//           geo
//           propertyType
//           section
//           transactionType
//           uri
//         }
//       }
//       townsAndCities {
//         category
//         geo
//         propertyType
//         section
//         transactionType
//         rows {
//           links {
//             category
//             geo
//             propertyType
//             section
//             transactionType
//             uri
//           }
//         }
//         links {
//           category
//           geo
//           propertyType
//           section
//           transactionType
//           uri
//         }
//       }
//     }
//   }
// ... on SeoResultsError {
//     errorCode
//   }
// }
import { fetch } from '@zoopla/data/core';

// TODO: autogen
type PropertyIdMappingServiceResponse = {
    property_id: string;
};

async function getPropertyId(uprn: string): Promise<string | null> {
    if (!process.env.API_PROPERTY_ID_MAPPING_URL) {
        throw new Error('Missing envar');
    }

    const url = new URL(`${process.env.API_PROPERTY_ID_MAPPING_URL}/${uprn}`);

    const result = await fetch<PropertyIdMappingServiceResponse>({
        url
    });
    return result?.property_id?.toString() ?? null;
}

const buildRetrocompatiblePath = async (
    path: string
): Promise<string | null> => {
    if (!/\/uprn\/\d+/.test(path)) return path;
    const uprn = path.match(/\/uprn\/(?<uprn>\d+)/)?.groups?.uprn;

    if (!uprn) {
        return null;
    }

    const propertyId = await getPropertyId(uprn);
    if (propertyId) return `/property/${propertyId}`;
    // logger.info(
    //     { 'method.name': 'buildRetrocompatiblePath' },
    //     'Cannot get SeoData: no property ID for UPRN'
    // );
    return null;
};

type Link = {
    category: string;
    geo: string;
    propertyType: string;
    section: string;
    transactionType: string;
    uri: string;
    listingCount?: number;
    numBeds?: string;
};

type InternalLink = {
    category: string;
    geo: string;
    propertyType: string;
    rows:
        | {
              links: Link[];
          }[]
        | null;
    links?: Link[] | null;
    section: string;
    transactionType: string;
};

// TODO: autogen
type SeoServiceResponse = {
    seoData: {
        breadcrumbs: {
            // we only use label and uri now?
            i18nLabelKey?: string;
            label: string | null;
            tracking?: string;
            uri?: string | null;
            noAppend?: boolean;
            geoType?: string;
            identifier?: string;
        }[];
        info: {
            areaExpansions: { level: number; name: string; url: string }[];
            areaName: string;
            canonicalUri: string;
            category?: string;
            section?: string;
        };
        internalLinks: {
            currentlyAvailable: InternalLink;
            townsAndCities: InternalLink | null;
        };
        onPage: {
            description: string;
            heading: string;
            title: string;
        };
        redirection: {
            code?: number | null;
            uri?: string | null;
        };
    };
    status: number;
};

export type SeoData = {
    breadcrumbs: {
        label: string;
        uri: string | null;
    }[];
    info: {
        areaExpansions: { level: number; name: string; url: string }[];
        areaName: string;
        canonicalUri: string;
        category: string;
        section: string;
    };
    internalLinks: {
        currentlyAvailable: InternalLink;
        townsAndCities: InternalLink;
    };
    onPage: {
        description: string;
        heading: string;
        title: string;
    };
};

export async function seo(path: string): Promise<SeoData | null> {
    if (!process.env.API_SEO || !process.env.API_SEO_SECRET) {
        throw new Error('Missing envar');
    }

    // TODO: speak to Chris about getting to one call?
    const seoPath = await buildRetrocompatiblePath(path);

    if (!seoPath) {
        return null;
    }

    const url = new URL(process.env.API_SEO);
    url.searchParams.append('uri', seoPath);
    url.searchParams.append(
        'with',
        'breadcrumbs,info,internal-links,on-page,redirection'
    );

    // TODO: Double check SEO data usage
    return fetch<SeoServiceResponse>({
        url,
        secret: process.env.API_SEO_SECRET
    }).then(({ seoData }) => {
        seoData.breadcrumbs.forEach((bc) => {
            if (!bc.label) {
                bc.label = null;
            }
            if (!bc.uri) {
                bc.uri = null;
            }
            delete bc.tracking;
            delete bc.i18nLabelKey;
            delete bc.noAppend;
            delete bc.geoType;
            delete bc.identifier;
        });

        // not requested by GraphQL
        delete seoData.info.category;
        delete seoData.info.section;

        // Does `currentlyAvailable` always have rows and `townsAndCities`
        // always have `links`?
        if (
            seoData.internalLinks.currentlyAvailable &&
            !seoData.internalLinks.currentlyAvailable.links
        ) {
            seoData.internalLinks.currentlyAvailable.links = null;
        }

        if (!seoData.internalLinks.townsAndCities) {
            seoData.internalLinks.townsAndCities = null;
        } else if (!seoData.internalLinks.townsAndCities.rows) {
            seoData.internalLinks.townsAndCities.rows = null;
        }

        seoData.internalLinks.currentlyAvailable?.links?.forEach((l) => {
            delete l.listingCount;
            delete l.numBeds;
        });

        seoData.internalLinks.townsAndCities?.links?.forEach((l) => {
            delete l.listingCount;
            delete l.numBeds;
        });

        seoData.internalLinks.currentlyAvailable?.rows?.forEach((row) => {
            row.links?.forEach((l) => {
                delete l.listingCount;
                delete l.numBeds;
            });
        });

        seoData.internalLinks.townsAndCities?.rows?.forEach((row) => {
            row.links?.forEach((l) => {
                delete l.listingCount;
                delete l.numBeds;
            });
        });

        seoData.redirection = {
            code: seoData.redirection?.code ?? null,
            uri: seoData.redirection?.uri ?? null
        };

        // TODO: type API call correctly
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        return seoData as SeoData;
    });
}
